'use client';

import { DEFAULT_RADIUS_KM } from './location-params';

/**
 * Buyer location preference persisted in localStorage so the marketplace can
 * re-apply "near you" browsing on the next visit (and so an explicit Clear
 * sticks — FB-Marketplace model). Client-only module.
 */

const STORAGE_KEY = 'marketplace:location';

export interface StoredLocation {
  city: string;
  lat: number;
  lng: number;
  radius: number;
}

type StoredValue = StoredLocation | { off: true };

export function readStoredLocation(): StoredValue | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredLocation> & {
      off?: boolean;
    };
    if (parsed.off === true) return { off: true };
    if (
      typeof parsed.city === 'string' &&
      typeof parsed.lat === 'number' &&
      typeof parsed.lng === 'number'
    ) {
      return {
        city: parsed.city,
        lat: parsed.lat,
        lng: parsed.lng,
        radius:
          typeof parsed.radius === 'number' ? parsed.radius : DEFAULT_RADIUS_KM,
      };
    }
    return null;
  } catch {
    // Storage unavailable (private mode quota, disabled) or corrupt JSON —
    // behave as "no preference".
    return null;
  }
}

export function saveStoredLocation(location: StoredLocation): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(location));
  } catch {
    // Non-fatal: the URL params still drive this visit.
  }
}

/** Explicit opt-out: the buyer cleared the location filter — don't re-detect. */
export function saveLocationOff(): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ off: true }));
  } catch {
    // Non-fatal.
  }
}
